import { useState, useMemo, useEffect, useCallback } from 'react';
import type { Farmer, FarmerFilters } from '../types/farmer';
import { useRBAC } from './usePermissions';
import { farmerService } from '../services/farmerService';

interface SortState {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface UseFarmersReturn {
  farmers: Farmer[];
  filteredFarmers: Farmer[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filters: FarmerFilters;
  setFilters: (filters: FarmerFilters) => void;
  viewAllOrgs: boolean;
  setViewAllOrgs: (viewAll: boolean) => void;
  sorting: SortState;
  setSorting: (sort: SortState) => void;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  totalFarmers: number;
  activeFarmers: number;
  inactiveFarmers: number;
  refreshFarmers: () => Promise<void>;
  fetchFarmers: (page?: number, customFilters?: FarmerFilters, pageSize?: number, viewAll?: boolean, sort?: SortState) => Promise<void>;
  updateFarmerOptimistically: (farmerId: string, updates: Partial<Farmer>) => void;
  deleteFarmerOptimistically: (farmerId: string) => void;
}

export const useFarmers = (): UseFarmersReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FarmerFilters>({});
  const [viewAllOrgs, setViewAllOrgs] = useState<boolean>(false);
  const [sorting, setSorting] = useState<SortState>({ sortBy: 'createdAt', sortOrder: 'desc' });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  
  const { hasPermission } = useRBAC();

  // Fetch farmers from backend
  const fetchFarmers = useCallback(async (
    page: number = pagination.page,
    customFilters: FarmerFilters = filters,
    pageSize: number = pagination.limit,
    viewAll: boolean = viewAllOrgs,
    sort: SortState = sorting
  ) => {
    if (!hasPermission('farmers:read')) {
      setError('You do not have permission to view farmers.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const options = {
        page,
        limit: pageSize,
        search: searchQuery || undefined,
        county: customFilters.county || undefined,
        crop: customFilters.crop || undefined,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        adminCode: customFilters.administrativeLevel?.value || undefined,
        adminLevel: customFilters.administrativeLevel?.level?.toString() || undefined,
        organizationId: customFilters.organizationId || undefined,
        viewAllOrgs: viewAll,
      };

      const response = await farmerService.getFarmers(options);
      
      setFarmers(response.farmers);
      setPagination(response.pagination);
    } catch (err) {
      console.error('useFarmers: Failed to fetch farmers:', err);
      setError('Failed to load farmers. Please try again.');
      setFarmers([]);
      setPagination({ page: 1, limit: 25, total: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  }, [hasPermission, searchQuery, filters, pagination.page, pagination.limit, sorting]);

  // Initial load
  useEffect(() => {
    const initialLoad = async () => {
      if (!hasPermission('farmers:read')) {
        setError('You do not have permission to view farmers.');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const options = {
          page: 1,
          limit: 25,
          search: undefined,
          county: undefined,
          crop: undefined,
          organizationId: undefined,
          viewAllOrgs: viewAllOrgs,
        };

        const response = await farmerService.getFarmers(options);
        
        setFarmers(response.farmers);
        setPagination(response.pagination);
      } catch (err) {
        console.error('useFarmers: Initial load failed:', err);
        setError('Failed to load farmers. Please try again.');
        setFarmers([]);
        setPagination({ page: 1, limit: 25, total: 0, totalPages: 0 });
      } finally {
        setLoading(false);
      }
    };

    initialLoad();
  }, [hasPermission]);

  // Refresh farmers (reload current page)
  const refreshFarmers = useCallback(async () => {
    await fetchFarmers(pagination.page, filters, pagination.limit, viewAllOrgs, sorting);
  }, [fetchFarmers, pagination.page, filters, pagination.limit, viewAllOrgs, sorting]);

  // Apply local search and filters to the loaded farmers
  const filteredFarmers = useMemo(() => {
    let result = farmers;

    // Apply local search if needed (server-side search is preferred)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(farmer => {
        const name = farmer.name?.toLowerCase() || '';
        const phone = farmer.phone?.toLowerCase() || '';
        const email = farmer.email?.toLowerCase() || '';
        const farmerCode = farmer.farmerCode?.toLowerCase() || '';
        const county = farmer.county?.toLowerCase() || '';
        const crop = farmer.crop?.toLowerCase() || '';
        
        return name.includes(query) ||
               phone.includes(query) ||
               email.includes(query) ||
               farmerCode.includes(query) ||
               county.includes(query) ||
               crop.includes(query);
      });
    }

    return result;
  }, [farmers, searchQuery]);

  // Calculate statistics
  const totalFarmers = pagination.total;
  const activeFarmers = farmers.filter(farmer => farmer.isActive).length;
  const inactiveFarmers = farmers.filter(farmer => !farmer.isActive).length;

  // New optimistic update methods
  const updateFarmerOptimistically = useCallback((farmerId: string, updates: Partial<Farmer>) => {
    setFarmers(prevFarmers => 
      prevFarmers.map(farmer => 
        farmer.id === farmerId 
          ? { ...farmer, ...updates, updatedAt: new Date().toISOString() }
          : farmer
      )
    );
  }, []);

  const deleteFarmerOptimistically = useCallback((farmerId: string) => {
    setFarmers(prevFarmers => 
      prevFarmers.filter(farmer => farmer.id !== farmerId)
    );
    
    setPagination(prevPagination => ({
      ...prevPagination,
      total: Math.max(0, prevPagination.total - 1),
    }));
  }, []);

  return {
    farmers,
    filteredFarmers,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    viewAllOrgs,
    setViewAllOrgs,
    sorting,
    setSorting,
    pagination,
    totalFarmers,
    activeFarmers,
    inactiveFarmers,
    refreshFarmers,
    fetchFarmers,
    updateFarmerOptimistically,
    deleteFarmerOptimistically,
  };
}; 